import React, { useState } from 'react';
import { Product } from '../types';
import { useCart } from '../hooks/useCart';

const EMOJI: Record<string, string> = {
  protection: '🛡️', tests: '🔬', 'soins-intimes': '💊',
};

interface Props {
  product:   Product;
  index:     number;
  onPreview: (p: Product) => void;
}

export default function ProductCard({ product, index, onPreview }: Props) {
  const { dispatch } = useCart();
  const [added, setAdded] = useState(false);

  const emoji     = EMOJI[product.categories?.slug || ''] || '📦';
  const price     = Number(product.price).toLocaleString('fr-FR');
  const shortDesc = product.short_description || product.description || '';

  const handleAdd = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    const minQty = product.min_quantity || 1;
    for (let i = 0; i < minQty; i++) dispatch({ type: 'ADD', product });
    setAdded(true);
    setTimeout(() => setAdded(false), 1200);
  };

  return (
    <div
      onClick={() => onPreview(product)}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--card-r)',
        overflow: 'hidden',
        cursor: 'pointer',
        animation: `fadeUp 0.4s ease ${Math.min(index, 5) * 0.07}s both`,
        WebkitAnimation: `fadeUp 0.4s ease ${Math.min(index, 5) * 0.07}s both`,
        /* Pas de transform hover sur mobile — juste active state */
        transition: 'border-color 0.2s',
      }}
    >
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        .product-card-img-wrap { position:relative; aspect-ratio:4/3; overflow:hidden; background:var(--surface2); display:flex; align-items:center; justify-content:center; font-size:2.5rem; }
      `}</style>

      {/* Image */}
      <div className="product-card-img-wrap">
        {product.image_url
          ? <img src={product.image_url} alt={product.name}
              style={{ width:'100%', height:'100%', objectFit:'cover' }}
              loading="lazy" />
          : <span>{emoji}</span>
        }
        {product.categories && (
          <span style={{
            position:'absolute', top:'0.5rem', left:'0.5rem',
            background:'rgba(8,10,12,0.8)', backdropFilter:'blur(8px)',
            border:'1px solid var(--border)', borderRadius:'2rem',
            padding:'0.15rem 0.55rem', fontSize:'0.62rem',
            letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--accent)',
          }}>
            {product.categories.name}
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding:'0.75rem 0.9rem 0.9rem' }}>
        <h3 style={{
          fontFamily:"'Cormorant Garamond',serif",
          fontSize:'1rem', fontWeight:400,
          marginBottom:'0.3rem', lineHeight:1.25,
        }}>
          {product.name}
        </h3>

        {shortDesc && (
          <p style={{
            fontSize:'0.75rem', color:'var(--muted)', lineHeight:1.5,
            marginBottom:'0.75rem',
            display:'-webkit-box', WebkitLineClamp:2,
            WebkitBoxOrient:'vertical', overflow:'hidden',
          }}>
            {shortDesc}
          </p>
        )}

        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.5rem' }}>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.15rem' }}>
            <span style={{
              fontFamily:"'Cormorant Garamond',serif",
              fontSize:'1.05rem', color:'var(--accent2)',
            }}>
              {price} F
            </span>
            {(product.min_quantity || 1) > 1 && (
              <span style={{ fontSize:'0.6rem', color:'var(--warning)', letterSpacing:'0.04em' }}>
                min. {product.min_quantity} unités
              </span>
            )}
          </div>

          <div style={{ display:'flex', gap:'0.4rem' }}>
            <button
              onClick={e => { e.stopPropagation(); onPreview(product); }}
              style={{
                height:'var(--touch-min)', padding:'0 0.75rem',
                background:'none', border:'1px solid var(--border)',
                color:'var(--muted)', borderRadius:'2rem',
                fontSize:'0.75rem', minWidth:'var(--touch-min)',
              }}
            >
              Voir
            </button>
            <button
              onClick={handleAdd}
              style={{
                height:'var(--touch-min)', padding:'0 0.9rem',
                background: added ? 'var(--success)' : 'var(--accent)',
                color:'var(--bg)', border:'none',
                borderRadius:'2rem', fontSize:'0.78rem', fontWeight:500,
                transition:'background 0.25s',
                minWidth:'var(--touch-min)',
              }}
            >
              {added ? '✓' : '+'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
